/**
 * One node, reached directly rather than through a miner.
 *
 * Renders the same panel the miner route does, so a node reached by its peer id
 * and a node reached through the standings read identically — but with no
 * attribution beside it, because there is none: nobody inferred anything to get
 * here, the address *is* the node.
 */

import { useEffect, useState } from 'react'

import type { NodeInfo } from '../api/generated/NodeInfo'
import { RequestFailed, fetchNode } from '../api/rest'
import { NodePanel } from './NodePanel'

export function NodeRoute({ chain, peer }: { chain: string; peer: string }) {
  const [node, setNode] = useState<NodeInfo | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    setNode(null)
    setError(null)
    fetchNode(chain, peer, controller.signal)
      .then(setNode)
      .catch((e: unknown) => {
        if (controller.signal.aborted) return
        setError(e instanceof RequestFailed ? e.message : 'Could not reach the observer.')
      })
    return () => controller.abort()
  }, [chain, peer])

  if (error) {
    return (
      <section className="panel" style={{ marginBottom: 26 }}>
        <div className="panel-head">
          <div>
            <div className="eyebrow">Node</div>
            <h2 className="panel-title" style={{ marginTop: 4, textTransform: 'none' }}>
              <code>{peer}</code>
            </h2>
          </div>
        </div>
        <p className="empty">{error}</p>
      </section>
    )
  }
  if (!node) return <p className="empty">Asking telemetry…</p>

  // No confidence, no votes, no attribution: this page was reached by naming
  // the node, so there is nothing inferred to qualify.
  return <NodePanel node={node} confidence={null} votes={null} attribution={null} />
}
